import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export interface Language {
  code: string;
  name: string;
  nativeName: string;
}

export const AVAILABLE_LANGUAGES: Language[] = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский' },
];

interface LanguageContextType {
  currentLanguage: Language;
  availableLanguages: Language[];
  changeLanguage: (languageCode: string) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState<Language>(() => {
    const savedLang = localStorage.getItem('takara_language') || i18n.language || 'en';
    return AVAILABLE_LANGUAGES.find((lang) => lang.code === savedLang) || AVAILABLE_LANGUAGES[0];
  });

  useEffect(() => {
    i18n.changeLanguage(currentLanguage.code);
  }, [currentLanguage, i18n]);

  const changeLanguage = (languageCode: string) => {
    const newLanguage = AVAILABLE_LANGUAGES.find((lang) => lang.code === languageCode);
    if (newLanguage) {
      setCurrentLanguage(newLanguage);
      localStorage.setItem('takara_language', languageCode);
      i18n.changeLanguage(languageCode);
    }
  };

  return (
    <LanguageContext.Provider
      value={{
        currentLanguage,
        availableLanguages: AVAILABLE_LANGUAGES,
        changeLanguage,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
